import { useEffect, useRef } from "react";

const solutions = [
  {
    icon: "ri-copyright-line",
    color: "#A855F7",
    glow: "rgba(168,85,247,0.2)",
    tag: "BRAND & IP",
    title: "브랜드 & IP 보호",
    desc: "상표권, 저작권 등록부터 브랜드 아이덴티티(BI) 설계까지. 당신의 창작물을 법적으로 보호하고 지속 가능한 브랜드로 포지셔닝합니다.",
    points: ["상표권 · 저작권 등록", "브랜드 아이덴티티 설계", "NFT 지식재산 전략"],
  },
  {
    icon: "ri-code-s-slash-line",
    color: "#14F1D9",
    glow: "rgba(20,241,217,0.2)",
    tag: "INFRASTRUCTURE",
    title: "핵심 인프라 구축",
    desc: "D2C 커머스 웹/앱, PG 결제 연동, 회원제 플랫폼까지. 아티스트 맞춤형 디지털 인프라를 A to Z로 설계하고 구축합니다.",
    points: ["D2C 쇼핑몰 · 앱 개발", "PG · 정기결제 연동", "프라이빗 멤버십 시스템"],
  },
  {
    icon: "ri-rocket-2-line",
    color: "#F97316",
    glow: "rgba(249,115,22,0.2)",
    tag: "GTM STRATEGY",
    title: "GTM 전략",
    desc: "시장 진입 전략 수립, 팝업 스토어 기획, 콜라보레이션 매칭까지. 당신의 IP가 세상과 만나는 최적의 경로를 설계합니다.",
    points: ["시장 진입 전략 수립", "팝업 전시 · 이벤트 기획", "파트너십 · 콜라보 매칭"],
  },
  {
    icon: "ri-building-4-line",
    color: "#C084FC",
    glow: "rgba(192,132,252,0.2)",
    tag: "BACK OFFICE",
    title: "운영 백오피스",
    desc: "법무 계약서 검토, 세무 · 회계 처리, 투자 유치 지원까지. 창작에만 집중할 수 있도록 복잡한 비즈니스 운영을 모두 대행합니다.",
    points: ["계약서 검토 · 법무 자문", "세무 · 회계 아웃소싱", "투자 유치 · IR 지원"],
  },
];

export default function CoreSolutions() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const cards = entry.target.querySelectorAll("[data-card]");
            cards.forEach((card, i) => {
              setTimeout(() => {
                (card as HTMLElement).style.opacity = "1";
                (card as HTMLElement).style.transform = "translateY(0)";
              }, i * 120);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="solutions" className="relative py-28 lg:py-44 px-6 bg-brand-bg overflow-hidden">
      {/* BG Decorations */}
      <div
        className="absolute top-0 left-1/2 -translate-x-1/2 w-px h-24 opacity-30"
        style={{ background: "linear-gradient(to bottom, transparent, rgba(168,85,247,0.8), transparent)" }}
      />
      <div
        className="absolute top-20 right-0 w-64 h-64 rounded-full opacity-10 pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(20,241,217,0.6) 0%, transparent 70%)" }}
      />

      <div ref={sectionRef} className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16 lg:mb-24">
          <span className="inline-block text-xs font-display font-medium tracking-widest uppercase text-brand-purple mb-5 px-3 py-1 rounded-full border border-brand-purple/30 bg-brand-purple/5">
            Core Solutions
          </span>
          <h2 className="font-display text-3xl md:text-5xl font-bold text-white leading-snug lg:leading-relaxed">
            예술을 비즈니스로 바꾸는<br />
            <span className="gradient-text-purple">4가지 핵심 솔루션</span>
          </h2>
          <p className="mt-5 text-white/50 font-body text-base max-w-xl mx-auto leading-loose">
            브랜딩부터 IT, 법무, 마케팅까지 — 크리에이터가 비즈니스에 집중할 수 있도록 모든 것을 지원합니다.
          </p>
        </div>

        {/* Cards Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-7">
          {solutions.map((sol, i) => (
            <div
              key={sol.tag}
              data-card
              style={{
                opacity: 0,
                transform: "translateY(24px)",
                transition: `opacity 0.6s ease ${i * 0.1}s, transform 0.6s ease ${i * 0.1}s`,
              }}
              className="group relative glass rounded-2xl p-6 lg:p-8 flex flex-col gap-5 lg:gap-7 hover:border-white/20 transition-all duration-300 cursor-default overflow-hidden"
            >
              {/* Hover glow bg */}
              <div
                className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-2xl"
                style={{ background: `radial-gradient(circle at 30% 30%, ${sol.glow} 0%, transparent 70%)` }}
              />

              {/* Icon + Tag row */}
              <div className="relative flex items-start justify-between gap-3">
                <div
                  className="w-12 h-12 flex items-center justify-center rounded-xl flex-shrink-0"
                  style={{ background: `${sol.glow}`, border: `1px solid ${sol.color}30` }}
                >
                  <i className={`${sol.icon} text-xl`} style={{ color: sol.color }} />
                </div>
                <span
                  className="relative text-xs font-display font-semibold tracking-widest mt-0.5"
                  style={{ color: sol.color }}
                >
                  {sol.tag}
                </span>
              </div>

              {/* Title & Desc */}
              <div className="relative flex flex-col gap-3">
                <h3 className="font-display text-lg font-bold text-white">{sol.title}</h3>
                <p className="text-white/50 text-sm font-body leading-loose">{sol.desc}</p>
              </div>

              {/* Points */}
              <ul className="relative flex flex-col gap-3 lg:gap-4 mt-auto">
                {sol.points.map((pt) => (
                  <li key={pt} className="flex items-center gap-2.5 text-sm text-white/60">
                    <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: sol.color }} />
                    {pt}
                  </li>
                ))}
              </ul>

              {/* Bottom border glow on hover */}
              <div
                className="absolute bottom-0 left-4 right-4 h-px opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                style={{ background: `linear-gradient(to right, transparent, ${sol.color}, transparent)` }}
              />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
